import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    AppBar,
    Toolbar,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import logo from './axentric_logo_icon.png';
import './App.css';

function HealthcarePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const features = [
        {
            icon: <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50' }} />,
            title: "Automated Patient Communication",
            description: "AI agents handle all patient emails, appointment confirmations, and follow-ups with personalized responses."
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#2196F3' }} />,
            title: "Financial Offer Generation",
            description: "Fast intelligent creation of financial offers tailored to individual patient needs based on doctor's assessments and patient's history."
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#2196F3' }} />,
            title: "Sales & Marketing",
            description: "Automated smart deeply personalized sales, marketing and patient engagement campaigns."
        },
        {
            icon: <ScheduleIcon sx={{ fontSize: 40, color: '#FF9800' }} />,
            title: "Scheduling & Planning",
            description: "Automated appointment scheduling, visits creation and planning, resource optimization and more."
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40, color: '#9C27B0' }} />,
            title: "Routine Tasks",
            description: "Automated routine tasks such as lab orders, accomodation reservations, driver bookings, external partner clinics communication and more."
        }
    ];

    const benefits = [
        "90% reduction in administrative screen time",
        "Faster patient response times",
        "Improved accuracy in financial calculations",
        "24/7 automated patient support",
        "Reduced human error in scheduling",
        "Enhanced patient satisfaction"
    ];

    return (
        <Box sx={{ 
            flexGrow: 1, 
            backgroundColor: '#050505', 
            minHeight: '100vh',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            {/* Header */}
            <AppBar position="static" sx={{ backgroundColor: 'transparent', color: 'white', boxShadow: 'none', mt: 2 }}>
                <Toolbar sx={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ 
                        width: { xs: 80, md: 100 }, 
                        height: { xs: 28, md: 35 },
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        <img 
                            src={logo} 
                            alt="Axentric Logo" 
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Hero Section */}
            <Box sx={{ 
                py: 8, 
                color: 'white',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '400px', sm: '500px', md: '800px' },
                    height: { xs: '300px', sm: '300px', md: '500px' },
                    background: 'radial-gradient(ellipse, rgba(59, 130, 246, 0.25) 0%, rgba(30, 64, 175, 0.20) 30%, rgba(139, 92, 246, 0.15) 60%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(60px)',
                    zIndex: 0
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: { xs: '15%', md: '20%' },
                    right: { xs: '5%', md: '10%' },
                    width: { xs: '250px', sm: '250px', md: '400px' },
                    height: { xs: '200px', sm: '200px', md: '300px' },
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.20) 0%, rgba(30, 64, 175, 0.15) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(45px)',
                    zIndex: 0
                }
            }}>
                {/* Additional gradient overlays for enhanced visual depth */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0
                }}>
                    {/* Left side accent gradient */}
                    <Box sx={{
                        position: 'absolute',
                        top: { xs: '25%', md: '30%' },
                        left: { xs: '2%', md: '5%' },
                        width: { xs: '120px', sm: '180px', md: '250px' },
                        height: { xs: '120px', sm: '180px', md: '250px' },
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(30, 64, 175, 0.06) 50%, transparent 100%)',
                        borderRadius: '50%',
                        filter: 'blur(20px)'
                    }} />
                    
                    {/* Top center accent gradient */}
                    <Box sx={{
                        position: 'absolute',
                        top: { xs: '8%', md: '10%' },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: { xs: '150px', sm: '200px', md: '300px' },
                        height: { xs: '100px', sm: '150px', md: '200px' },
                        background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%)',
                        borderRadius: '50%',
                        filter: 'blur(25px)'
                    }} />
                    
                    {/* Bottom left accent gradient */}
                    <Box sx={{
                        position: 'absolute',
                        bottom: { xs: '10%', md: '15%' },
                        left: { xs: '8%', md: '15%' },
                        width: { xs: '100px', sm: '140px', md: '180px' },
                        height: { xs: '100px', sm: '140px', md: '180px' },
                        background: 'radial-gradient(circle, rgba(30, 64, 175, 0.09) 0%, rgba(59, 130, 246, 0.05) 50%, transparent 100%)',
                        borderRadius: '50%',
                        filter: 'blur(20px)'
                    }} />
                    
                    {/* Mobile-specific top right gradient */}
                    <Box sx={{
                        position: 'absolute',
                        top: { xs: '20%', md: '25%' },
                        right: { xs: '3%', md: '8%' },
                        width: { xs: '80px', sm: '120px', md: '0px' },
                        height: { xs: '80px', sm: '120px', md: '0px' },
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.04) 50%, transparent 100%)',
                        borderRadius: '50%',
                        filter: 'blur(15px)',
                        display: { xs: 'block', md: 'none' }
                    }} />
                </Box>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h2" sx={{
                                mb: 3,
                                fontWeight: 'bold',
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                textAlign: { xs: 'center', md: 'left' }
                            }}>
                                Healthcare Administration
                            </Typography>
                            <Typography variant="h5" sx={{
                                mb: 4,
                                opacity: 0.9,
                                lineHeight: 1.6,
                                textAlign: { xs: 'center', md: 'left' },
                                fontSize: { xs: '1.2rem', md: '1.5rem' }
                            }}>
                                Transform your healthcare practice with AI-powered automation that handles 90% of administrative tasks.
                            </Typography>
                            <Typography variant="body1" sx={{
                                mb: 4,
                                lineHeight: 1.7,
                                opacity: 0.8,
                                textAlign: { xs: 'center', md: 'left' },
                                fontSize: { xs: '1rem', md: '1.1rem' }
                            }}>
                                Admins spend 90% of their time on emails, offers, scheduling, and insurance. Axentric's AI agents team up with them to automate these tasks, allowing your staff to focus on what matters most - patient care.
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                width: '100%',
                                height: { xs: 300, md: 400 },
                                borderRadius: 3,
                                overflow: 'hidden',
                                backgroundColor: '#0a0e10'
                            }}>
                                <img 
                                    src={require('./images/financial_offer_creation.png')} 
                                    alt="Healthcare AI Automation" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '12px'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg" sx={{ px: { xs: 6, sm: 2 } }}>
                    <Typography variant="h3" sx={{
                        mb: 3,
                        textAlign: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '2rem', md: '2.5rem' }
                    }}>
                        The future of healthcare administration
                    </Typography>
                    
                    <Typography variant="h6" sx={{
                        mb: 6,
                        textAlign: 'center',
                        color: 'rgba(255, 255, 255, 0.9)',
                        maxWidth: 800,
                        mx: 'auto',
                        lineHeight: 1.6,
                        fontSize: { xs: '1.1rem', md: '1.3rem' }
                    }}>
                        Axentric platform enables deeply personalized task and service execution at scale, regardless of clinic size, by offloading all major administrative tasks to AI agents.
                    </Typography>
                    
                    <Grid container spacing={{ xs: 10, sm: 3 }} justifyContent="center">
                        {features.map((feature, index) => (
                            <Grid item xs={11} sm={6} lg={4} key={index}>
                                <Box sx={{
                                    height: '100%',
                                    background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.6) 0%, rgba(26, 31, 37, 0.6) 100%)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    borderRadius: 4,
                                    px: 3,
                                    pt: 3,
                                    pb: 1.5,
                                    transition: 'all 0.4s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.3)',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.8) 0%, rgba(26, 31, 37, 0.8) 100%)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: 'linear-gradient(90deg, #4CAF50, #2196F3, #FF9800, #9C27B0)',
                                        opacity: { xs: 1, sm: 0 },
                                        transition: 'opacity 0.3s ease'
                                    },
                                    '&:hover::before': {
                                        opacity: 1
                                    }
                                }}>
                                    <Typography variant="h6" sx={{
                                        mb: 2,
                                        color: 'white',
                                        fontWeight: '700',
                                        fontSize: { xs: '1.2rem', sm: '1.3rem', lg: '1.4rem' },
                                        textAlign: { xs: 'center', sm: 'left' },
                                        lineHeight: 1.3
                                    }}>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.95rem', sm: '1rem', lg: '1.05rem' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        {feature.description}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Reliability & Observability Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h3" sx={{
                                mb: 4,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                textAlign: { xs: 'center', md: 'left' }
                            }}>
                                100% Reliability and Observability
                            </Typography>
                            <Typography variant="h6" sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: 1.7,
                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                textAlign: { xs: 'center', md: 'left' }
                            }}>
                                AI agents, while autonomous, always involve humans before taking any major actions. Administrators remain in the loop, providing approval and oversight, while AI handles the execution and operational tasks.
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                width: '100%',
                                height: { xs: 300, md: 400 },
                                borderRadius: 3,
                                overflow: 'hidden',
                                backgroundColor: '#0a0e10'
                            }}>
                                <img 
                                    src={require('./images/AI_confirmation.png')} 
                                    alt="AI Confirmation and Human Oversight Interface" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '12px'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* AI Training Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    {/* Main Heading */}
                    <Typography variant="h3" sx={{
                        mb: 6,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        textAlign: 'center'
                    }}>
                        Powered by Axentric core platform
                    </Typography>

                    {/* Two Subsections */}
                    <Grid container spacing={12}>
                        {/* Section 1: Teaching AI agents on how to behave */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 4,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.3) 0%, rgba(26, 31, 37, 0.3) 100%)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                                }
                            }}>
                                <Typography variant="h4" sx={{
                                    mb: 3,
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: { xs: '1.4rem', md: '1.6rem' },
                                    textAlign: { xs: 'center', md: 'left' }
                                }}>
                                    Teach your AI agents on how to behave
                                </Typography>
                                <Typography variant="body1" sx={{
                                    mb: 4,
                                    lineHeight: 1.7,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontSize: { xs: '1rem', md: '1.1rem' }
                                }}>
                                    Every clinic is unique. Teach your AI agents to understand your specific business processes, communication style, and operational workflows.
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    mt: 3
                                }}>
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.15) 100%)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#8b5cf6', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Communication
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Customize communication styles and tone to match your clinic's personality and patient interaction preferences.
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.15) 100%)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#8b5cf6', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Behaviour
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Independent reactions and situation handling based on your clinic's protocols and decision-making frameworks.
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.15) 100%)',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#8b5cf6', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Business Process
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Knowledge about your clinic's procedures, workflows, and way of working to ensure seamless integration.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Section 2: Train AI agents on specific deep tasks */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 4,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.3) 0%, rgba(26, 31, 37, 0.3) 100%)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                                }
                            }}>
                                <Typography variant="h4" sx={{
                                    mb: 3,
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: { xs: '1.4rem', md: '1.6rem' },
                                    textAlign: { xs: 'center', md: 'left' }
                                }}>
                                    Train your AI agents for deep tasks
                                </Typography>
                                <Typography variant="body1" sx={{
                                    mb: 4,
                                    lineHeight: 1.7,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontSize: { xs: '1rem', md: '1.1rem' }
                                }}>
                                    Train your AI agents to handle complex, specialized tasks that require deep domain and business knowledge and sophisticated decision-making.
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    mt: 3
                                }}>
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.15) 100%)',
                                        border: '1px solid rgba(33, 150, 243, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(33, 150, 243, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#2196F3', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Financial Offer Creation
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Train AI agents to understand your dental services portfolio, pricing structures, and how your staff communicates with patients about financial matters and payment options.
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.15) 100%)',
                                        border: '1px solid rgba(33, 150, 243, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(33, 150, 243, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#2196F3', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Scheduling
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Train AI agents about your personnel, their specialties, procedures and services, typical duration estimates, and how procedures are optimally divided through available time slots.
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.15) 100%)',
                                        border: '1px solid rgba(33, 150, 243, 0.2)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(33, 150, 243, 0.2) 100%)',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)'
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#2196F3', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            mb: 1.5
                                        }}>
                                            Sales & Marketing
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem'
                                        }}>
                                            Train AI agents to understand your clinic's sales funnels, marketing campaigns, promotional strategies, and how to effectively communicate value propositions to potential patients.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Personalization Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        p: 4,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.4) 0%, rgba(26, 31, 37, 0.4) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Typography variant="h3" sx={{
                            mb: 4,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '1.8rem' }
                        }}>
                            Fully Personalized Software Solutions
                        </Typography>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            color: 'rgba(255, 255, 255, 0.9)',
                            lineHeight: 1.6,
                            fontSize: { xs: '1.1rem', md: '1.3rem' },
                            maxWidth: 900,
                            mx: 'auto'
                        }}>
                            Each client software is completely personalized and can be adapted to your clinic's specific needs by leveraging the Axentric core platform powered by AI - from interface customization to special functionalities.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Benefits Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{
                        mb: 6,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        textAlign: 'center'
                    }}>
                        Unlock more value in your healthcare business
                    </Typography>
                    
                    <Grid container spacing={2} justifyContent="center">
                        {[
                            'Amplify admin productivity',
                            'Less admins, more patients',
                            'Happier staff',
                            'More time for face-to-face patient care',
                            'Better and faster service',
                            'Less human errors',
                            'Faster patient response times',
                            'Boost conversion rates'
                        ].map((benefit, index) => (
                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                <Box sx={{
                                    background: 'linear-gradient(145deg, rgba(30, 37, 43, 0.7) 0%, rgba(26, 31, 37, 0.7) 50%, rgba(20, 25, 30, 0.7) 100%)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: 4,
                                    px: 2.5,
                                    py: 2.5,
                                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.01)',
                                        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        background: 'linear-gradient(145deg, rgba(40, 47, 53, 0.9) 0%, rgba(36, 41, 47, 0.9) 50%, rgba(30, 35, 40, 0.9) 100%)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '-1px',
                                        left: '-1px',
                                        right: '-1px',
                                        bottom: '-1px',
                                        background: 'linear-gradient(45deg, #4CAF50, #2196F3, #FF9800, #9C27B0, #4CAF50)',
                                        borderRadius: 5,
                                        zIndex: -1,
                                        opacity: 0,
                                        transition: 'opacity 0.5s ease'
                                    },
                                    '&:hover::before': {
                                        opacity: 0.3
                                    },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)',
                                        pointerEvents: 'none'
                                    }
                                }}>
                                    <Box sx={{
                                        position: 'relative',
                                        zIndex: 1
                                    }}>
                                        <Typography variant="body1" sx={{
                                            color: 'white',
                                            fontWeight: '500',
                                            fontSize: { xs: '0.95rem', sm: '1rem', lg: '1.05rem' },
                                            textAlign: { xs: 'center', sm: 'left' },
                                            lineHeight: 1.3,
                                            mb: 0
                                        }}>
                                            {benefit}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="md">
                    <Card sx={{
                        background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.9) 0%, rgba(26, 31, 37, 0.9) 100%)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 4,
                        p: 6,
                        textAlign: 'center'
                    }}>
                        <Typography variant="h4" sx={{
                            mb: 3,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '2.2rem' }
                        }}>
                            Ready to Transform Your Practice?
                        </Typography>
                        <Typography variant="body1" sx={{
                            mb: 4,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            lineHeight: 1.6
                        }}>
                            Reach out to us to learn more about how Axentric Healthcare can help your clinic.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: 'black',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': { backgroundColor: '#333' }
                            }}
                        >
                            Contact Us
                        </Button>
                    </Card>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ py: 4, color: 'white', textAlign: 'center' }}>
                <Typography variant="body2">
                    Copyright © 2025 Axentric. All rights reserved.
                </Typography>
            </Box>
        </Box>
    );
}

export default HealthcarePage; 